import { makeEnum, makeEnumWithData, makeOptions,
  makeEnumWithDataAndLabels,
  setupClass, lookupData, Matches,
  interpolateInMap, doubleInterpolateInMap,
  IdsMap, PleaseContactStr,
  IntervalTimer,
} from '../Base.js'
import * as ser from '../Common/SerUtil.js'
import { InputComponent } from '../Common/InputComponent.js'
import { ComponentArray } from '../Common/ComponentArray.js'

import { Field, FieldType, } from '../Common/Field.js'
import { Space } from './Space.js'
import { removeElem } from '../SharedUtils.js'

import { gApp, DebugOn } from '../Globals.js'

export class ZoneSpace {
  init() {
    this.spaceType = Field.makeTypeSelect('Type', gApp.proj().spaces, null, {
      errorWhenEmpty: `You must create a Space`,
      metadata: {
        typeClass: Space,
      }
    });
    this.quantity = new Field({
      name: 'Quantity',
      type: FieldType.Count,
      defaultValue: 1,
      min: 1,
      allowMin: true,
    })

    this.serFields = [
      'spaceType',
      'quantity',
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _name: "Space",
      path: 'zone-space',
    }
  }

  getSpaceType() {
    return this.spaceType.lookupValue();
  }

  getSpaceName() {
    return this.spaceType.lookupValue().getName();
  }

  getQuantity() {
    return this.quantity.value;
  }

  isOccupied(hr) {
    return this.spaceType.lookupValue().isOccupied(hr);
  }
}
setupClass(ZoneSpace)

export class Zone extends InputComponent {
  init(name) {
    this.name = Field.makeName(`Zone Name`, name)

    this.spaces = new ComponentArray(ZoneSpace)

    this.winterIndoorTemp = new Field({
      name: 'Winter indoor temp',
      type: FieldType.Temp,
      defaultValue: 70,
      metricDefaultValue: 21,
    });
    this.summerIndoorTemp = new Field({
      name: 'Summer indoor temp',
      type: FieldType.Temp,
      defaultValue: 75,
      metricDefaultValue: 24,
    });

    this.humiditySetpoint = new Field({
      name: 'Zone Humidity',
      type: FieldType.Percent,
      defaultValue: 50,
    });

    this.tempFields = [
      'winterIndoorTemp',
      'summerIndoorTemp',
      'humiditySetpoint',
    ]

    this.serFields = [
        'name',
        ser.arrayField('spaces', () => { return ZoneSpace.create(); }),
        'winterIndoorTemp',
        'summerIndoorTemp',
        'humiditySetpoint',
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _uniqueName: true,
      _name: () => {
        return this.name.value;
      },
      path: 'zone',
    }
  }

  setupDefaults() {
    // Add a default space
    if (this.spaces.length === 0) {
      this.createSpace();
    }
  }

  getName() {
    return this.name.value;
  }

  getObjErrors() {
    let errors = [];
    this.updater.addErrors(errors);
    if (this.spaces.length === 0) {
      errors.push('You must add at least one Space to the Zone.');
    }
    for (const name of this.getDuplicateSpaceNames()) {
      errors.push(`Space '${name}' is added to this Zone more than once.`);
    }
    return errors;
  }

  // Names of Spaces that are used by more than one ZoneSpace in this Zone.
  getDuplicateSpaceNames() {
    let seenIds = new Set();
    let duplicateNames = new Set();
    for (const zoneSpace of this.spaces) {
      let id = zoneSpace.spaceType.value;
      if (id == null) {
        continue;
      }
      if (seenIds.has(id)) {
        duplicateNames.add(zoneSpace.getSpaceName());
      } else {
        seenIds.add(id);
      }
    }
    return [...duplicateNames];
  }

  createSpace() {
    let zoneSpace = ZoneSpace.create();
    this.spaces.push(zoneSpace);
    return zoneSpace;
  }

  getSpaceAtIndex(spaceIndex) {
    return this.spaces[spaceIndex];
  }

  isOccupied(hr) {
    // The zone is occupied if any space is occupied
    for (const space of this.spaces) {
      if (space.isOccupied(hr)) {
        return true;
      }
    }
    return false;
  }

  getSpaceQuantities() {
    // Return an array of the space quantities
    return this.spaces.map(space => space.quantity.value);
  }

  hasSpaceWithId(spaceId) {
    for (const space of this.spaces) {
      if (space.spaceType.value === spaceId) {
        return true;
      }
    }
    return false;
  }

  addSpaceWithId(spaceId) {
    if (this.hasSpaceWithId(spaceId)) {
      return;
    }
    let zoneSpace = this.createSpace();
    zoneSpace.spaceType.value = spaceId;
  }

  removeSpaceWithId(spaceId) {
    // TODO: We should really enforce that the same spaceId cannot be used across
    // multiple ZoneSpaces in a Zone, but that's not worth fixing right now. For now,
    // just remove every ZoneSpace with this spaceId, since there may be more than one.
    let matches = this.spaces.filter(space => space.spaceType.value === spaceId);
    for (const zoneSpace of matches) {
      removeElem(this.spaces, zoneSpace);
    }
  }
}
setupClass(Zone)